import UserAvatar from "@/components/images/UserAvatar";
import LoadingWrapper from "@/components/state-handlers/LoadingOverlay";
import { Avatar, AvatarFallback } from "@capacit-com/lara-law-shared-ui";
import { Button } from "@capacit-com/lara-law-shared-ui";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { Permission } from "@/lib/permissions/Permission.enum";
import { useHasPermission } from "@/lib/permissions/permissionFunctions";
import { Group } from "@/models/feature/feature";
import { User } from "@/models/users/User";
import { formatUsersName } from "@/utils/format";
import { Plus, X } from "lucide-react";
import { FC } from "react";

interface UserListProps {
  users?: User[];
  groups?: Group[];
  removingUsers?: string[];
  removingGroups?: string[];
  onAddUser?: (userId: string) => void;
  onRemoveUser?: (userId: string) => void;
  onAddGroup?: (groupId: string) => void;
  onRemoveGroup?: (groupId: string) => void;
  className?: string;
}

const UserList: FC<UserListProps> = ({
  users,
  groups,
  removingUsers,
  removingGroups,
  onAddUser,
  onRemoveUser,
  onAddGroup,
  onRemoveGroup,
  className,
}) => {
  const hasPermission = useHasPermission();
  const computedClassName = className ?? "flex-1 my-2";

  const getGroupDeleteOverlay = (groupId: string) => {
    if (removingGroups?.includes(groupId)) {
      return <LoadingWrapper text="Fjerner gruppe..." />;
    }
    return null;
  };

  return (
    <div
      className={`overflow-auto pl-4 pr-1 py-2 mr-1 ${computedClassName} min-h-0`}
    >
      <div
        className={` ${
          users?.length === 0 || groups?.length === 0
            ? "flex flex-col"
            : "grid grid-cols-1 gap-y-2"
        }`}
      >
        {users?.map((user) => (
          <div
            key={user.id}
            className="relative"
            style={{
              paddingLeft: "2px",
            }}
          >
            {removingUsers?.includes(user.id) && (
              <LoadingWrapper text="Fjerner bruger..." />
            )}
            <div className="flex flex-row items-center gap-2 text-sm min-w-0">
              <UserAvatar
                user={user}
                avatarClassName="size-7 shrink-0"
                imageClassName="size-7"
                fallbackClassName="size-7 text-[10px]"
              />
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="truncate min-w-0 flex-1">
                    {formatUsersName(user)}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p className="font-medium">{formatUsersName(user)}</p>
                  {user.email && (
                    <p className="text-muted-foreground text-xs mt-0.5">
                      {user.email}
                    </p>
                  )}
                </TooltipContent>
              </Tooltip>
              {hasPermission({ allOf: [Permission.UpdateFeatureGroups] }) &&
                onAddUser && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-2 shrink-0 ml-auto opacity-50 hover:opacity-100"
                    onClick={() => onAddUser(user.id)}
                  >
                    <Plus className="size-4" />
                  </Button>
                )}
              {hasPermission({ allOf: [Permission.UpdateFeatureGroups] }) &&
                onRemoveUser && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-2 shrink-0 ml-auto opacity-50 hover:opacity-100"
                    onClick={() => onRemoveUser(user.id)}
                  >
                    <X className="size-4" />
                  </Button>
                )}
            </div>
          </div>
        ))}
        {users?.length === 0 && (
          <p className="text-sm text-muted-foreground flex justify-center items-center h-full">
            Ingen brugere i gruppen
          </p>
        )}
        {groups?.map((group) => (
          <div key={group.id} className="relative">
            {getGroupDeleteOverlay(group.id)}
            <div
              key={group.id}
              className="flex flex-row items-center gap-2 text-sm"
            >
              <Avatar className="size-7 shrink-0">
                <AvatarFallback className="size-7">
                  {group.name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <span className="truncate grow">{group.name}</span>
              {onAddGroup && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-2 shrink-0 ml-auto opacity-50 hover:opacity-100"
                  onClick={() => onAddGroup(group.id)}
                >
                  <Plus className="size-4" />
                </Button>
              )}
              {onRemoveGroup && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-2 shrink-0 ml-auto opacity-50 hover:opacity-100"
                  onClick={() => onRemoveGroup(group.id)}
                >
                  <X className="size-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
        {groups?.length === 0 && (
          <p className="text-sm text-muted-foreground flex justify-center items-center h-full">
            Ingen grupper
          </p>
        )}
      </div>
    </div>
  );
};

export default UserList;
